import type { QualityGateIssue, QualityGateVerdict } from './api/types.js';

/** What an edit did to the verdict's standing (2026-09-29).
 *
 *  Every post-edit refresh stamps `edit_refreshed_at` and the deterministic
 *  checks it re-ran. With the stamp, "stale" narrows to the findings those
 *  checks do not cover — the judge's — and the operator can keep applying
 *  fixes. Without it (an older verdict, or a failed refresh) the whole verdict
 *  is stale, as before.
 *
 *  MEM (f381617c): the old banner said "Regenerate to re-evaluate" after the
 *  refresh had already re-checked the edit, and fix-apply stayed disabled
 *  after the first applied fix, so the hold looked as if it had no action. */
export interface EditRecheck {
  stale: boolean;
  recheckedAfterEdit: boolean;
  recheckedSkills: string[];
  /** Open findings from skills the refresh did not re-run. */
  notRejudged: number;
  /** Fix-apply stays available once the edit has been re-checked. */
  batchAllowed: boolean;
}

export function editRecheck(
  metadata: QualityGateVerdict,
  needsDecision: (issue: QualityGateIssue) => boolean,
): EditRecheck {
  const stale = metadata.stale_after_edit === true;
  const recheckedAfterEdit = stale && !!metadata.edit_refreshed_at;
  const recheckedSkills = metadata.edit_refresh_checks ?? [];
  const skills = new Set(recheckedSkills);
  const notRejudged = recheckedAfterEdit
    ? (metadata.issues || []).filter((i) => needsDecision(i) && !skills.has(i.source_skill)).length
    : 0;
  return {
    stale,
    recheckedAfterEdit,
    recheckedSkills,
    notRejudged,
    batchAllowed: !stale || recheckedAfterEdit,
  };
}
